<template>
  <AuthLayout
    :title="layoutTitle"
    :subtitle="layoutSubtitle"
    :show-tabs="!getCode && !forgetPass"
    active-tab="login"
    @back="toMain"
    @tab-change="onTabChange"
  >
    <!-- Login -->
    <form
      v-if="!getCode && !forgetPass"
      class="auth-panel__form"
      @submit.prevent="Login"
    >
      <AuthInput
        v-model="email"
        :label="$t('authEmailOrPhone')"
        type="email"
        :placeholder="$t('mail')"
        :errors="emailErrors"
      >
        <template #icon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,12 2,6" />
          </svg>
        </template>
      </AuthInput>

      <AuthInput
        v-model="password"
        :label="$t('authPasswordLabel')"
        type="password"
        :placeholder="$t('pass')"
        :errors="passwordErrors"
      />

      <div class="auth-panel__row">
        <label class="auth-panel__remember">
          <input type="checkbox" />
          <span>{{ $t('remember') }}</span>
        </label>
        <button type="button" class="auth-panel__link" @click="forgetPass = true">
          {{ $t('forgetPass') }}
        </button>
      </div>

      <button class="auth-panel__submit" type="submit" :disabled="loginLoading">
        <span v-if="loginLoading" class="auth-spinner" />
        {{ loginLoading ? '...' : $t('authSignInArrow') }}
      </button>

      <!-- Social login — temporarily disabled
      <div class="auth-panel__divider"><span>{{ $t('authOrLogin') }}</span></div>
      <div class="auth-panel__socials">
        <button type="button" class="auth-social-btn" disabled>Google</button>
        <button type="button" class="auth-social-btn" disabled>Telegram</button>
      </div>
      -->

      <p class="auth-panel__footer">
        {{ $t('authNoAccount') }}
        <router-link to="/registration-guest" class="auth-panel__footer-link">
          {{ $t('authTabRegister') }}
        </router-link>
      </p>

      <p class="auth-panel__agreement">
        {{ $t('conf1') }}
        <a :href="DOCS?.doc_1">{{ $t('conf2') }}</a>
        {{ $t('and') }}
        <a :href="DOCS?.doc_2">{{ $t('conf3') }}</a>
      </p>
    </form>

    <!-- Forgot password -->
    <form v-else-if="forgetPass" class="auth-panel__form" @submit.prevent="passForget">
      <div class="auth-panel__top-bar">
        <button type="button" aria-label="back" @click="forgetPass = false">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>
      </div>

      <AuthInput
        v-model="email"
        :label="$t('authEmailLabel')"
        type="email"
        :placeholder="$t('mail')"
        :errors="emailErrors"
      >
        <template #icon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,12 2,6" />
          </svg>
        </template>
      </AuthInput>

      <button class="auth-panel__submit" type="submit" :disabled="loading">
        <span v-if="loading" class="auth-spinner" />
        {{ $t('withoutPass') }}
      </button>

      <p class="auth-panel__agreement" style="text-align: center">{{ $t('correctMail') }}</p>
    </form>

    <!-- SMS code -->
    <form v-else class="auth-panel__form" @submit.prevent="sendCode">
      <div class="auth-panel__top-bar">
        <button type="button" aria-label="back" @click="getCode = false">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>
      </div>

      <AuthInput
        v-model="code"
        :label="$t('sms')"
        type="text"
        :placeholder="$t('sms')"
      />

      <button class="auth-panel__submit" type="submit">{{ $t('confirm') }}</button>
      <p class="auth-panel__agreement" style="text-align: center">{{ $t('correctPhone') }}</p>
    </form>
  </AuthLayout>
</template>

<script>
import { mask } from 'vue-the-mask';
import axios from 'axios';
import { mapActions } from 'vuex';
import { createToaster } from '@meforma/vue-toaster';
import useVuelidate from '@vuelidate/core';
import { required, minLength, helpers, email } from '@vuelidate/validators';
import { createEcho } from '@/plugins/echo';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import AuthInput from '@/components/auth/AuthInput.vue';
import { AUTH_DOCS } from '@/constants/authDocs';

const toaster = createToaster({ position: 'top-right', duration: 2000 });

export default {
  name: 'LoginPage',
  components: { AuthLayout, AuthInput },
  directives: { mask },
  data() {
    return {
      v$: useVuelidate(),
      sendSms: false,
      getCode: false,
      forgetPass: false,
      email: '',
      password: '',
      phone: '',
      code: '',
      loading: false,
      loginLoading: false,
      DOCS: { ...AUTH_DOCS },
    };
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage(this.$t('req'), required),
        email: helpers.withMessage(this.$t('reqMail'), email),
      },
      password: {
        required: helpers.withMessage(this.$t('req'), required),
        minLength: helpers.withMessage(this.$t('minLength'), minLength(6)),
      },
      phone: {
        required: helpers.withMessage('Обязательное поле', required),
        minLength: helpers.withMessage('Обязательное поле', minLength(11)),
      },
    };
  },
  computed: {
    layoutTitle() {
      if (this.forgetPass) return this.$t('sbros');
      if (this.getCode) return this.$t('sign');
      return this.$t('authWelcome');
    },
    layoutSubtitle() {
      if (this.forgetPass) return this.$t('authForgotSub');
      if (this.getCode) return this.$t('correctPhone');
      return this.$t('authWelcomeSub');
    },
    emailErrors() {
      if (!this.v$.email.$dirty && !this.v$.email.$error) return [];
      return this.v$.email.$errors.map((e) => e.$message);
    },
    passwordErrors() {
      if (!this.v$.password.$dirty && !this.v$.password.$error) return [];
      return this.v$.password.$errors.map((e) => e.$message);
    },
  },
  methods: {
    ...mapActions(['requestUser']),
    onTabChange(tab) {
      if (tab === 'register') {
        this.$router.push('/registration-guest');
      }
    },
    async Login() {
      const [emailOk, passwordOk] = await Promise.all([
        this.v$.email.$validate(),
        this.v$.password.$validate(),
      ]);
      if (!emailOk || !passwordOk) {
        toaster.error(this.$t('err'));
        return;
      }

      this.loginLoading = true;
      try {
        const res = await axios.post('/V1/login', {
          email: this.email,
          password: this.password,
          type: 'email',
        });

        if (res.status === 200) {
          localStorage.setItem('access_token', res.data.token);
          const echo = createEcho(res.data.token);
          if (echo) {
            this.$root.$echo = echo;
          }

          await this.requestUser();
          const role = this.$store.state.role;
          toaster.success(this.$t('succesSign'));

          if (role === 'user') {
            const home = this.$store.state.hasApprovedPrograms ? '/platform' : '/programs';
            this.$router.push(home);
          } else if (role === 'guest') {
            this.$router.push('/platform');
          } else if (role === 'curator' || role === 'expert') {
            this.$router.push('/curator');
          } else if (role === 'supervisor' || role === 'chief') {
            this.$router.push('/supervisor');
          } else {
            this.$router.push('/');
          }
        }
      } catch (err) {
        console.error(err);
        toaster.error(err?.response?.data?.message ?? this.$t('err'));
      } finally {
        this.loginLoading = false;
      }
    },
    toMain() {
      this.$router.push('/');
    },
    passForget() {
      if (!this.email) {
        toaster.error('Email is required.');
        return;
      }
      this.loading = true;
      axios
        .post(
          '/V1/forget-password',
          {
            email: this.email,
            type: 'email',
          },
          {
            headers: {
              Authorization: localStorage.getItem('access_token')
                ? `Bearer ${localStorage.getItem('access_token')}`
                : null,
            },
          }
        )
        .then((res) => {
          this.loading = false;
          if (res.status === 200) {
            this.forgetPass = false;
            toaster.success(this.$t('sendMail'));
          }
        })
        .catch((err) => {
          this.loading = false;
          this.forgetPass = false;
          if (err.response?.data?.retry_after > 0) {
            toaster.error(
              this.$t('retryAfter.start') +
                err.response.data.retry_after +
                this.$t('retryAfter.end')
            );
          }
        });
    },
    async sendSmsPhone() {
      await this.v$.$validate();
      axios
        .post(`/V1/login/send-code`, {
          phone: this.phone,
        })
        .then((res) => {
          if (res.status === 200) {
            toaster.success('Успешно отправлено!');
            this.getCode = true;
          }
        })
        .catch((err) => {
          toaster.error(this.$t('err'));
          console.log(err);
        });
    },
    async sendCode() {
      axios
        .post(`/V1/login/auth-code`, {
          phone: this.phone,
          code: this.code,
        })
        .then((res) => {
          if (res.status === 200) {
            localStorage.setItem('access_token', res.data.token);
            toaster.success(this.$t('succesSign'));
            this.$router.push('/platform/cabinet');
          }
        })
        .catch((err) => {
          toaster.error(this.$t('err'));
          console.log(err);
        });
    },
  },
};
</script>
